"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbsoluteDate = void 0;
const tslib_1 = require("tslib");
const errors_1 = require("@zap-onboard/errors");
const dayjs_1 = (0, tslib_1.__importDefault)(require("dayjs"));
const duration_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/duration"));
const relativeTime_1 = (0, tslib_1.__importDefault)(require("dayjs/plugin/relativeTime"));
const TimeAndDate_1 = require("./TimeAndDate");
dayjs_1.default.extend(duration_1.default);
dayjs_1.default.extend(relativeTime_1.default);
/**
 * Models birthdays, anniversary style dates that should not be modified based
 * on a users timezone
 */
class AbsoluteDate {
    constructor(year, month, day) {
        this.year = year;
        this.month = month;
        this.day = day;
    }
    static fromDotNetString(dns) {
        return AbsoluteDate.fromDate(TimeAndDate_1.TimeAndDate.fromDotNetString(dns)).validate();
    }
    static now() {
        return AbsoluteDate.fromDate(new Date());
    }
    static create(args) {
        return new AbsoluteDate(args.year, args.month, args.day).validate();
    }
    /**
     * Used to accept dates from time sensitive inputs that return offsetted time.
     */
    static fromOffsettedDate(date) {
        const [day, month, year] = date
            .toLocaleDateString('en-AU')
            .split('/')
            .map(Number);
        return new AbsoluteDate(year, month, day).validate();
    }
    static fromDate(date) {
        return new AbsoluteDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()).validate();
    }
    static fromUserInput(input) {
        const date = AbsoluteDate.parseUserInput(input);
        if (date?.validationMessage()) {
            return undefined;
        }
        return date;
    }
    static validationMessageFromUserInput(input) {
        if (typeof input !== 'string') {
            return 'Invalid date';
        }
        const date = AbsoluteDate.parseUserInput(input);
        if (!date) {
            return 'Invalid date';
        }
        return date?.validationMessage();
    }
    static parseUserInput(input) {
        const numberGroups = (input.match(/[0-9]+/g) ?? [])
            .map(Number)
            .filter((i) => i);
        if (numberGroups.length !== 3) {
            return undefined;
        }
        const [day, month, year] = numberGroups;
        let finalYear = year;
        if (year < 100) {
            const currentYearSuffix = new Date().getFullYear() - 2000;
            // TODO: Make this code work after the year 2100
            if (year <= currentYearSuffix) {
                finalYear += 2000;
            }
            else {
                finalYear += 1900;
            }
        }
        const date = new AbsoluteDate(finalYear, month, day);
        return date;
    }
    static fromJSON(dateString) {
        const datePieces = dateString.split('-').map(Number).filter(Boolean);
        if (datePieces.length !== 3) {
            throw errors_1.errs.ValidationError.create(`[${dateString}] is not a valid date string`);
        }
        return new AbsoluteDate(datePieces[0], datePieces[1], datePieces[2]).validate();
    }
    toTimeFromNow() {
        return (0, dayjs_1.default)(this.toDate()).fromNow();
    }
    add(time) {
        return AbsoluteDate.fromDate(TimeAndDate_1.TimeAndDate.fromDate(this.toDate()).add(time));
    }
    subtract(time) {
        return AbsoluteDate.fromDate(TimeAndDate_1.TimeAndDate.fromDate(this.toDate()).subtract(time));
    }
    prettyValues() {
        return {
            day: `${this.day}`.padStart(2, '0'),
            month: `${this.month}`.padStart(2, '0'),
            year: `${this.year}`,
        };
    }
    validationMessage() {
        try {
            this.validate();
            return undefined;
        }
        catch (e) {
            return e?.message ?? 'Invalid Date';
        }
    }
    validate() {
        const { month, day, year } = this;
        if (year < 1000 || year > 2100) {
            throw errors_1.errs.ValidationError.create(`${year} is not a valid year`);
        }
        if (month < 1 || month > 12) {
            throw errors_1.errs.ValidationError.create(`${month} is not a valid month`);
        }
        if (day < 1 || day > daysInMonth(year, month)) {
            throw errors_1.errs.ValidationError.create(`${day} is not a valid day`);
        }
        return this;
    }
    isInPast() {
        return this.toDate().valueOf() < new Date().valueOf();
    }
    toDate() {
        const { year, month, day } = this;
        return new Date(year, month - 1, day);
    }
    toDotNetString() {
        // Add time padding to reduce amount of errors in birthday submissions to
        // Xero
        return `/Date(${this.toDate().getTime() + 10000})/`;
    }
    asJSON() {
        const { year, month, day } = this;
        return `${year}-${month}-${day}`;
    }
    asMMYY() {
        const { year, month } = this;
        return `${month}/${year}`;
    }
    asUserFacingFormat() {
        const { year, month, day } = this;
        return `${day}/${month}/${year}`;
    }
    asDMYFormat() {
        const { year, month, day } = this;
        return `${day}-${month}-${year}`;
    }
    monthName() {
        return monthName[this.month.toString()];
    }
    ordinalDay() {
        const { day } = this;
        if (day > 3 && day < 21) {
            return `${day}th`;
        }
        switch (day % 10) {
            case 1:
                return `${day}st`;
            case 2:
                return `${day}nd`;
            case 3:
                return `${day}rd`;
            default:
                return `${day}th`;
        }
    }
    asLongPretty() {
        return `${this.ordinalDay()} ${this.monthName()} ${this.year}`;
    }
    asShortPretty() {
        if (this.year === AbsoluteDate.now().year) {
            return `${this.monthName()} ${this.day}`;
        }
        return `${this.monthName()} ${this.day} ${this.year}`;
    }
    asYYYYMMDD() {
        const { year, month, day } = this;
        return `${year.toString().padStart(4, '0')}-${month
            .toString()
            .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    }
    asDDMonthYYYY() {
        return (0, dayjs_1.default)(this.toDate()).format('D MMMM YYYY');
    }
    asDDMMYYYY() {
        return (0, dayjs_1.default)(this.toDate()).format('DD/MM/YYYY');
    }
}
exports.AbsoluteDate = AbsoluteDate;
const monthName = {
    1: 'Jan',
    2: 'Feb',
    3: 'March',
    4: 'Apr',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'Aug',
    9: 'Sep',
    10: 'Oct',
    11: 'Nov',
    12: 'Dec',
};
function daysInMonth(year, month) {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return month === 2 && year % 4 === 0 ? 29 : days[month - 1];
}
